/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './RiskScanDemo.module.scss';

import type { RiskScanDemoProps } from './RiskScanDemo.types';

type Severity = 'high' | 'med' | 'crit';

const ROWS: { label: string; value: string; sev: Severity; sevLabel: string }[] = [
  { label: 'Incorporation date', value: '26 days ago', sev: 'high', sevLabel: 'High risk' },
  { label: 'Ownership history', value: 'UBO changed 12 days ago', sev: 'high', sevLabel: 'High risk' },
  { label: 'Registered address', value: '213 entities at this address', sev: 'med', sevLabel: 'Medium risk' },
  { label: 'Financial filings', value: 'No accounts filed to date', sev: 'med', sevLabel: 'Medium risk' },
  { label: 'PEPs & sanctions', value: '1 potential match — director', sev: 'crit', sevLabel: 'Critical' },
];

export const RiskScanDemo = ({ className }: RiskScanDemoProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { threshold: 0.35 });

  const runIdRef = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const [passShown, setPassShown] = useState(false);
  const [dividerShown, setDividerShown] = useState(false);
  const [revealed, setRevealed] = useState(0);
  const [settled, setSettled] = useState(0);
  const [verdictShown, setVerdictShown] = useState(false);
  const [done, setDone] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const wait = useCallback(
    (ms: number, id: number) =>
      new Promise<void>((resolve, reject) => {
        const t = setTimeout(() => (runIdRef.current === id ? resolve() : reject()), ms);
        timers.current.push(t);
      }),
    [],
  );

  const showFinalState = useCallback(() => {
    clearTimers();
    setPassShown(true);
    setDividerShown(true);
    setRevealed(ROWS.length);
    setSettled(ROWS.length);
    setVerdictShown(true);
    setDone(true);
  }, [clearTimers]);

  const play = useCallback(async () => {
    clearTimers();
    const id = ++runIdRef.current;

    setPassShown(false);
    setDividerShown(false);
    setRevealed(0);
    setSettled(0);
    setVerdictShown(false);
    setDone(false);

    try {
      await wait(500, id);
      setPassShown(true);
      await wait(700, id);
      setDividerShown(true);

      for (let i = 0; i < ROWS.length; i += 1) {
        await wait(450, id);
        setRevealed(i + 1);
        await wait(560, id);
        setSettled(i + 1);
      }

      await wait(500, id);
      setVerdictShown(true);
      setDone(true);
    } catch {
      // superseded run — ignore
    }
  }, [clearTimers, wait]);

  useEffect(() => {
    if (reduced) {
      showFinalState();

      return;
    }

    if (inView) {
      play();
    }

    return clearTimers;
  }, [inView, reduced, play, showFinalState, clearTimers]);

  const replay = useCallback(() => {
    if (reduced) return;
    play();
  }, [reduced, play]);

  return (
    <div ref={ref} className={cn(styles.stage, className)} aria-label="Company risk check demo">
      <div className={styles.demo}>
        <div className={styles.head}>
          <span className={styles.dots}>
            <i />
            <i />
            <i />
          </span>
          <span className={styles.headTitle}>globaldatabase.com — risk check</span>
          <span className={styles.live}>LIVE</span>
        </div>

        <div className={styles.body}>
          <div className={styles.target}>
            <b>Novatek Supplies Ltd</b>
            <span className={cn(styles.status, done && styles.statusDone)}>{done ? '5 red flags' : 'Verifying…'}</span>
          </div>

          <div className={cn(styles.pass, passShown && styles.show)}>
            <span className={styles.passCheck}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path
                  d="M2.5 6.3l2.1 2.1L9.5 3.4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <div>
              <b>Bank account verified — name matched</b>
              <span>IBAN valid · account holder matches · the payment would clear</span>
            </div>
          </div>

          <div className={cn(styles.divider, dividerShown && styles.show)}>
            <span>Now checking the company behind the account</span>
          </div>

          <div className={styles.rows}>
            {ROWS.map((row, i) => {
              const isSettled = i < settled;

              return (
                <div key={row.label} className={cn(styles.row, i < revealed && styles.show)}>
                  <span className={styles.rowLabel}>{row.label}</span>
                  <span className={cn(styles.rowValue, !isSettled && styles.rowValuePending)}>
                    {isSettled ? (
                      row.value
                    ) : (
                      <>
                        <span className={styles.spinner} />
                        Scanning…
                      </>
                    )}
                  </span>
                  <span className={cn(styles.sev, styles[row.sev], isSettled && styles.sevOn)}>{row.sevLabel}</span>
                </div>
              );
            })}
          </div>

          <div className={cn(styles.verdict, verdictShown && styles.show)}>
            <b>Do not release payment</b>
            <span>The account passed, but the company behind it failed the risk check.</span>
          </div>
        </div>

        {done && !reduced && (
          <button type="button" className={styles.replay} onClick={replay}>
            ↻ Replay
          </button>
        )}
      </div>
    </div>
  );
};
